import { Button, EmptyState, Icon } from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbChartBar, TbLayoutSidebar, TbLayoutSidebarRight } from "react-icons/tb";

import { cn } from "@/lib/utils";
import { useDimensionDefinitions } from "@/queries/dimension-definition.queries";
import type { DimensionDefinitionType } from "@/types/dimension-definition.types";
import { ValueType } from "@/types/dimension-definition.types";
import { APP_ROUTES_FULL } from "@/utils/routes";

import { DimensionSelectorPanel } from "./components/dimension-selector-panel";
import { EntityManagementPanel } from "./components/entity-management-panel";
import { QualityWatch24hChart } from "./components/quality-watch-24h-chart";
import { QualityWatchChart } from "./components/quality-watch-chart";
import { QualityWatchTopBar } from "./components/quality-watch-top-bar";
import { useQualityWatch } from "./hooks/use-quality-watch";
import { useQualityWatchChartView } from "./hooks/use-quality-watch-chart-view";
import {
    getDimensionsForQualityWatchPanel,
    isValidDimensionId,
    selectPreferredDimension,
} from "./utils/dimension-selection.utils";
import {
    CHART_PERIODS,
    DEFAULT_CHART_HEIGHT,
    MAX_CHART_HEIGHT,
    MIN_CHART_HEIGHT,
} from "./utils/quality-watch.constants";

export const Route = createFileRoute(APP_ROUTES_FULL.PERFORMANCE_QUALITY_WATCH)({
    component: PerformanceQualityWatch,
});

function PerformanceQualityWatch() {
    const { t } = useTranslation();
    const {
        period,
        selectedDate,
        selectedDefinitionIds,
        setSelectedDefinitionIds,
        selectedCallPurposes,
        selectedEntities,
        getVisibleEntities,
        getEntitiesWithColors,
    } = useQualityWatch();

    const [isEntityPanelOpen, setIsEntityPanelOpen] = useState(true);
    const [isDimensionPanelOpen, setIsDimensionPanelOpen] = useState(true);

    const { data: dimensionDefsResponse } = useDimensionDefinitions({
        page: 1,
        limit: 100,
        search: ValueType.ONE_TO_TEN,
    });

    const allDimensionDefs: DimensionDefinitionType[] = useMemo(() => {
        const data = dimensionDefsResponse?.data;
        return Array.isArray(data) ? data : [];
    }, [dimensionDefsResponse]);

    useEffect(() => {
        if (allDimensionDefs.length === 0) return;

        // In 24h mode the panel shows only the preferred dimension; keep selection in sync
        if (period === CHART_PERIODS.TWENTY_FOUR_HOURS) {
            const preferred = selectPreferredDimension(allDimensionDefs);
            if (preferred && (selectedDefinitionIds.length !== 1 || selectedDefinitionIds[0] !== preferred.id)) {
                setSelectedDefinitionIds([preferred.id]);
            }
            return;
        }

        if (selectedDefinitionIds.length === 0) {
            const preferred = selectPreferredDimension(allDimensionDefs);
            if (preferred) setSelectedDefinitionIds([preferred.id]);
        }
        const validSelections = selectedDefinitionIds.filter((id) => isValidDimensionId(id, allDimensionDefs));
        if (validSelections.length !== selectedDefinitionIds.length) {
            if (validSelections.length === 0) {
                const preferred = selectPreferredDimension(allDimensionDefs);
                if (preferred) setSelectedDefinitionIds([preferred.id]);
            } else {
                setSelectedDefinitionIds(validSelections);
            }
        }
    }, [allDimensionDefs, selectedDefinitionIds, period]);

    const chartView = useQualityWatchChartView({
        period,
        selectedDate,
        selectedDefinitionIds,
        selectedCallPurposes,
        selectedEntities,
        allDimensionDefs,
        getEntitiesWithColors,
    });

    const visibleEntities = getVisibleEntities();
    const [chartHeight, setChartHeight] = useState(DEFAULT_CHART_HEIGHT);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const calculateHeight = () => {
            const topBarHeight = 100;
            const padding = 32;
            const legendHeight = chartView.entityCountForLegend > 1 ? 120 : 0;
            const buffer = 48;
            const calculatedHeight = window.innerHeight - topBarHeight - padding - legendHeight - buffer;
            setChartHeight(Math.max(MIN_CHART_HEIGHT, Math.min(MAX_CHART_HEIGHT, calculatedHeight)));
        };
        calculateHeight();
        window.addEventListener("resize", calculateHeight);
        return () => window.removeEventListener("resize", calculateHeight);
    }, [chartView.entityCountForLegend]);

    const showEmptyState = visibleEntities.length === 0 || !chartView.canShowChart;

    return (
        <div className="flex flex-col h-full w-full bg-background">
            <QualityWatchTopBar
                isLoading={chartView.isLoading}
                onRefresh={chartView.refetch}
                visibleEntitiesCount={visibleEntities.length}
            />

            <div className="flex flex-1 min-h-0 overflow-hidden relative">
                <div
                    className={`shrink-0 border-r border-border transition-all duration-300 ease-in-out ${
                        isEntityPanelOpen ? "w-64" : "w-0 overflow-hidden"
                    }`}
                >
                    <div className="h-full">
                        <EntityManagementPanel
                            isOpen={isEntityPanelOpen}
                            onToggle={() => setIsEntityPanelOpen(!isEntityPanelOpen)}
                        />
                    </div>
                </div>

                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsEntityPanelOpen(true)}
                    className={cn(
                        "absolute left-0 top-4 z-10",
                        isEntityPanelOpen
                            ? "opacity-0 pointer-events-none -translate-x-full"
                            : "opacity-100 translate-x-0"
                    )}
                    aria-label={t("show_entities_panel")}
                >
                    <Icon icon={TbLayoutSidebar} />
                </Button>

                <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                    <div className="flex-1 min-h-0 p-4 overflow-auto">
                        {showEmptyState ? (
                            <div className="h-full flex items-center justify-center">
                                <EmptyState
                                    icon={<TbChartBar size={32} className="text-muted-foreground" />}
                                    title={
                                        visibleEntities.length === 0 ? "no_entities_selected" : "no_dimensions_selected"
                                    }
                                    description={
                                        visibleEntities.length === 0
                                            ? "add_entities_to_start_comparison"
                                            : "select_dimensions_to_view_data"
                                    }
                                    actionText={
                                        visibleEntities.length === 0
                                            ? "click_plus_button_to_add_entities"
                                            : "select_dimensions_from_right_panel"
                                    }
                                />
                            </div>
                        ) : chartView.viewMode === "24h" ? (
                            <div className="h-full">
                                <QualityWatch24hChart
                                    key={chartView.chartKey}
                                    {...chartView.chartProps24h}
                                    height={chartHeight}
                                />
                            </div>
                        ) : (
                            <div className="h-full">
                                <QualityWatchChart
                                    key={chartView.chartKey}
                                    {...chartView.chartPropsAvg}
                                    height={chartHeight}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Sidebar - Dimension Selector */}
                <div
                    className={`shrink-0 border-l border-border transition-all duration-300 ease-in-out ${
                        isDimensionPanelOpen ? "w-64" : "w-0 overflow-hidden"
                    }`}
                >
                    <div className="h-full">
                        <DimensionSelectorPanel
                            isOpen={isDimensionPanelOpen}
                            onToggle={() => setIsDimensionPanelOpen(!isDimensionPanelOpen)}
                            dimensions={
                                period === CHART_PERIODS.TWENTY_FOUR_HOURS
                                    ? getDimensionsForQualityWatchPanel(allDimensionDefs)
                                    : allDimensionDefs
                            }
                        />
                    </div>
                </div>

                {/* Right Sidebar Toggle Button - Always visible when closed */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsDimensionPanelOpen(true)}
                    className={cn(
                        "absolute right-0 top-4 z-10",
                        isDimensionPanelOpen
                            ? "opacity-0 pointer-events-none translate-x-full"
                            : "opacity-100 translate-x-0"
                    )}
                    aria-label={t("show_dimensions_panel")}
                >
                    <Icon icon={TbLayoutSidebarRight} />
                </Button>
            </div>
        </div>
    );
}
